import { css } from "@emotion/react";
import { FC, FormEvent, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  Alert,
  Button,
  DesignToken,
  IPickerListItem,
  Input,
  ModalPortal,
  Picker,
  SpacingToken,
  Tooltip,
} from "@livechat/design-system-react-components";

import { AdaptedModal as Modal } from "components/design-system-adapters/AdaptedModal";
import { Icon, IconsEnum } from "components/icon";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import Text from "components/layout/Text";
import { AgentPickerListItem, AgentPickerSelectedListItem } from "components/picker-items/agentPickerItems";
import { TeamPickerListItem, TeamPickerSelectedListItem } from "components/picker-items/teamPickerItems";
import { config } from "config/config";
import { IAgent, IWithAgentsSessionState, IWithAgentsState, selectAgentsAlphabetically } from "store/entities/agents";
import { selectAreMailboxesSetUp } from "store/entities/email-domains";
import { selectDefaultMailboxDescription, selectDefaultTeamId, selectLicenseId } from "store/entities/license";
import { IMailbox } from "store/entities/mailboxes/interfaces";
import {
  selectIsCreatingMailbox,
  selectIsUpdatingDefaultMailbox,
  selectIsUpdatingMailbox,
  selectMailboxes,
} from "store/entities/mailboxes/selectors";
import { ITeam, selectTeamsAlphabetically } from "store/entities/teams";
import { IWithRequestsState } from "store/requests/selectors";
import { SettingsViewActions, selectNextForwardingAddress } from "store/views/settings";
import {
  ICreateMailboxPayload,
  IUpdateDefaultMailboxPayload,
  IUpdateMailboxPayload,
} from "store/views/settings/interfaces";
import { fullWidthInput } from "styles/common";
import { StackingContextLevel } from "styles/stacking-context-level";
import { withVar } from "themes/helpers";

import { FormFieldNoMargin } from "../../FormFieldNoMargin";

import { ForwardingAddressDisplay } from "./ForwardingAddressDisplay";
import {
  footerButtonGroupStyles,
  footerButtonStyles,
  inputLabelStyles,
  pickerLabelStyles,
  tooltipIconStyles,
} from "./styles";

interface ISetupInboxModalFooterProps {
  onCancel: () => void;
  isCreate: boolean;
  loading: boolean;
  disabled: boolean;
}

const UNASSIGNED_OPTION_KEY = "unassigned";

const getTeamPickerListItem = ({ id, name, isDefault }: ITeam): IPickerListItem => ({
  key: id,
  name: name,
  customElement: {
    listItemBody: <TeamPickerListItem name={name} isDefault={isDefault} pickerType="single" />,
    selectedItemBody: <TeamPickerSelectedListItem name={name} isDefault={isDefault} pickerType="single" />,
  },
});

const getAgentPickerListItem = ({ id, name, email, avatar }: IAgent, loggedInAgentId?: string): IPickerListItem => ({
  key: id,
  name: name,
  customElement: {
    listItemBody: (
      <AgentPickerListItem
        agentName={name}
        avatarUrl={avatar}
        isLoggedInAgent={id === loggedInAgentId}
        secondaryText={email}
      />
    ),
    selectedItemBody: <AgentPickerSelectedListItem agentName={name} avatarUrl={avatar} />,
  },
});

const getUnassignedAgentPickerListItem = (): IPickerListItem => ({
  key: UNASSIGNED_OPTION_KEY,
  name: "Unassigned",
  customElement: {
    listItemBody: <AgentPickerListItem agentName="Unassigned" avatarUrl="" secondaryText="" />,
    selectedItemBody: <AgentPickerSelectedListItem agentName="Unassigned" avatarUrl="" />,
  },
});

const SetupInboxModalFooter: FC<ISetupInboxModalFooterProps> = ({ onCancel, isCreate, loading, disabled }) => (
  <div css={footerButtonGroupStyles}>
    <Button css={footerButtonStyles} kind="secondary" onClick={onCancel}>
      Cancel
    </Button>
    <Button
      css={footerButtonStyles}
      type="submit"
      kind="primary"
      disabled={disabled}
      form="setup-inbox-modal-form"
      loading={loading}
      data-testid="setup-inbox-modal-submit-button"
    >
      {isCreate ? "Add" : "Update"}
    </Button>
  </div>
);

export interface ISetupInboxModalProps {
  editingInboxID?: string;
  onClose: () => void;
  onInboxSetup: (payload: IUpdateMailboxPayload | ICreateMailboxPayload | IUpdateDefaultMailboxPayload) => void;
}

const AddressDescriptionTooltip: FC = () => (
  <div
    css={css`
      width: fit-content;
    `}
  >
    <Tooltip
      kind="invert"
      placement="right"
      triggerRenderer={
        <span css={tooltipIconStyles}>
          <Icon
            name={IconsEnum.Info}
            width="14px"
            height="14px"
            inline
            fill={withVar(DesignToken.ContentBasicPrimary)}
          />
        </span>
      }
    >
      Add a description to know from which email addresses the messages will be forwarded.
    </Tooltip>
  </div>
);

const SetupInboxModal: FC<ISetupInboxModalProps> = ({ editingInboxID, onClose }) => {
  const dispatch = useDispatch();

  const teams = useSelector(selectTeamsAlphabetically);
  const agents = useSelector((store: IWithAgentsState & IWithAgentsSessionState) =>
    selectAgentsAlphabetically(store, {
      excludeViewers: true,
      activeOnly: true,
    })
  );
  const licenseId = useSelector(selectLicenseId);
  const defaultInboxDescription = useSelector(selectDefaultMailboxDescription);
  const isDefaultActive = useSelector(selectAreMailboxesSetUp);
  const inboxes = useSelector(selectMailboxes);
  const defaultTeamId = useSelector(selectDefaultTeamId);
  const nextName = useSelector(selectNextForwardingAddress);

  const isUpdatingDefault = useSelector(selectIsUpdatingDefaultMailbox);
  const [prevIsUpdatingDefault, setPrevIsUpdatingDefault] = useState(isUpdatingDefault);

  const isDefaultUpdateSuccessful = useSelector(state =>
    selectIsUpdatingDefaultMailbox(state as IWithRequestsState, true)
  );

  const isCreating = useSelector(selectIsCreatingMailbox);
  const [prevIsCreating, setPrevIsCreating] = useState(isCreating);

  const isCreatingSuccessful = useSelector(state => selectIsCreatingMailbox(state as IWithRequestsState, true));

  const isUpdating = useSelector(selectIsUpdatingMailbox);
  const [prevIsUpdating, setPrevIsUpdating] = useState(isUpdating);

  const isUpdatingSuccessful = useSelector(state => selectIsUpdatingMailbox(state as IWithRequestsState, true));

  useEffect(() => {
    if (
      (isCreatingSuccessful && prevIsCreating && !isCreating) ||
      (isUpdatingSuccessful && prevIsUpdating && !isUpdating) ||
      (isDefaultUpdateSuccessful && prevIsUpdatingDefault && !isUpdatingDefault)
    ) {
      onClose();
      return;
    }

    setPrevIsCreating(isCreating);
    setPrevIsUpdating(isUpdating);
    setPrevIsUpdatingDefault(isUpdatingDefault);
  }, [
    isCreatingSuccessful,
    isCreating,
    prevIsCreating,
    isUpdatingSuccessful,
    isUpdating,
    prevIsUpdating,
    isDefaultUpdateSuccessful,
    isUpdatingDefault,
    prevIsUpdatingDefault,
    onClose,
  ]);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const onInboxSetup = (payload: IUpdateMailboxPayload | ICreateMailboxPayload) => {
    if ((payload as IUpdateMailboxPayload).id !== undefined && editingInboxID !== "default") {
      dispatch(SettingsViewActions.updateMailbox(payload as IUpdateMailboxPayload));
    } else if (editingInboxID === "default") {
      dispatch(SettingsViewActions.updateDefaultMailboxSettings({ description: payload.description }));
    } else {
      dispatch(SettingsViewActions.createMailbox(payload));
    }
  };

  const defaultInbox: IMailbox = {
    id: "default",
    name: "",
    teamId: defaultTeamId,
    description: defaultInboxDescription,
    flags: {
      inboxUsed: isDefaultActive,
    },
  };

  const inbox = editingInboxID === "default" ? defaultInbox : editingInboxID ? inboxes[editingInboxID] : undefined;

  const isLoading = isUpdatingDefault || isUpdating || isCreating;

  const [teamId, setTeamId] = useState<string>(inbox ? inbox.teamId : defaultTeamId || "");
  const [agentId, setAgentId] = useState<string>(inbox?.agentId || "");
  const [description, setDescription] = useState<string>(inbox?.description || "");
  const [descriptionValidation, setDescriptionValidation] = useState("");

  const validateDescription = useCallback((value: string) => {
    if (value.length > 250) {
      setDescriptionValidation("Description is too long");
    }
  }, []);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const teamAgents = agents.filter((agent: any) => agent.teamIds.includes(teamId));
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const selectedAgent = teamAgents.find((agent: any) => agent.id === agentId);
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const selectedTeam = teams.find((team: any) => team.id === teamId);

  const handleSetupModal = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      onInboxSetup({
        id: inbox?.id,
        teamId,
        agentId,
        description,
      });
    },
    [agentId, description, inbox, onInboxSetup, teamId]
  );

  const forwardingAddressValue = inbox
    ? `${[licenseId, inbox.name].filter(Boolean).join("-")}@${config.defaultMailboxSuffix}`
    : `${[licenseId, nextName].filter(Boolean).join("-")}@${config.defaultMailboxSuffix}`;

  return (
    <ModalPortal zIndex={StackingContextLevel.Modal}>
      <Modal
        onClose={onClose}
        heading={inbox ? "Edit forwarding address" : "Add forwarding address"}
        footer={
          <SetupInboxModalFooter
            loading={isLoading}
            isCreate={!inbox}
            disabled={descriptionValidation.length > 0}
            onCancel={onClose}
          />
        }
      >
        <form id="setup-inbox-modal-form" data-testid="setup-inbox-modal-form" onSubmit={handleSetupModal}>
          <Flex gap={withVar(SpacingToken.Spacing8)} column>
            <Flex gap={withVar(SpacingToken.Spacing4)} column>
              <ForwardingAddressDisplay inbox={inbox} forwardingAddress={forwardingAddressValue} />
              <FormFieldNoMargin
                // @ts-expect-error TS(2322)
                labelText={
                  <Text css={inputLabelStyles}>
                    <Flex gap={withVar(SpacingToken.Spacing2)}>
                      Email address name <AddressDescriptionTooltip />
                    </Flex>
                  </Text>
                }
                error={descriptionValidation}
              >
                <Input
                  id="description"
                  name="description"
                  type="text"
                  value={description}
                  css={[fullWidthInput]}
                  // FIXME: Fix this the next time the file is edited.
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  onChange={(event: any) => {
                    setDescription(event.target.value);
                    validateDescription(event.target.value);
                  }}
                  placeholder="Enter your current email address from which you'll forward emails"
                  data-testid="description-input"
                />
              </FormFieldNoMargin>
            </Flex>
            <Flex gap={withVar(SpacingToken.Spacing4)} column>
              <Text css={pickerLabelStyles}>Assign tickets from this forwarding address to:</Text>
              {inbox && inbox.id === "default" && (
                <Block maxWidth="560px">
                  <Alert>
                    Default forwarding address can&lsquo;t be assigned to agents or non-default team. Create a separate
                    address to set up assignments.
                  </Alert>
                </Block>
              )}
              <Flex column maxWidth="560px">
                <FormFieldNoMargin labelText="Team">
                  <Picker
                    data-testid="setup-inbox-modal-team-picker"
                    maxListHeight={230}
                    hideClearButton
                    options={teams.map(getTeamPickerListItem)}
                    selected={selectedTeam ? [getTeamPickerListItem(selectedTeam)] : []}
                    disabled={inbox?.id === "default"}
                    onSelect={(selectedTeams: IPickerListItem[] | null) => {
                      if (selectedTeams) {
                        setTeamId(selectedTeams[0].key);
                        setAgentId("");
                      }
                    }}
                  />
                </FormFieldNoMargin>
              </Flex>
              <Flex column maxWidth="560px">
                <FormFieldNoMargin labelText="Agent">
                  <Picker
                    maxListHeight={240}
                    hideClearButton
                    options={[
                      getUnassignedAgentPickerListItem(),
                      ...teamAgents.map(agent => getAgentPickerListItem(agent)),
                    ]}
                    selected={[
                      selectedAgent ? getAgentPickerListItem(selectedAgent) : getUnassignedAgentPickerListItem(),
                    ]}
                    disabled={inbox?.id === "default"}
                    onSelect={(selectedAgents: IPickerListItem[] | null) => {
                      if (selectedAgents) {
                        const agentId = selectedAgents[0].key;

                        setAgentId(agentId === UNASSIGNED_OPTION_KEY ? "" : selectedAgents[0].key);
                      }
                    }}
                  />
                </FormFieldNoMargin>
              </Flex>
            </Flex>
          </Flex>
        </form>
      </Modal>
    </ModalPortal>
  );
};

export default SetupInboxModal;
